/**
 * Normalizes number input for HTML number inputs and form validation.
 *
 * Plain decimals with "." or "," are supported independently of locale: "1.5", "1,5".
 * Grouped numbers are interpreted for the current locale. For example,
 * "1.290,21" normalizes to "1290.21" in de-DE.
 */
const integerInputPattern = /^-?\d+$/;
const simpleDecimalInputPattern = /^-?\d+(?:[.,]\d+)?$/;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getNumberSeparators = (locale: string) => {
    const numberParts = new Intl.NumberFormat(locale).formatToParts(1234567.89);
    return {
        decimal: numberParts.find(part => part.type === 'decimal')?.value ?? '.',
        group: numberParts.find(part => part.type === 'group')?.value ?? ',',
    };
};

const getGroupSeparatorPattern = (groupSeparator: string) =>
    groupSeparator.trim() === '' ? '[ \\u00A0\\u202F]' : escapeRegExp(groupSeparator);

const normalizeLocaleGroupedNumber = (
    value: string,
    separators: { decimal: string; group: string },
    allowDecimal: boolean
) => {
    const { decimal, group } = separators;
    if (group === decimal) {
        return undefined;
    }

    const groupPattern = getGroupSeparatorPattern(group);
    const decimalPattern = escapeRegExp(decimal);
    const groupedIntegerPattern = `-?\\d{1,3}(?:${groupPattern}\\d{3})+`;
    const groupedNumberPattern = new RegExp(
        `^${groupedIntegerPattern}${allowDecimal ? `(?:${decimalPattern}\\d+)?` : ''}$`
    );
    if (!groupedNumberPattern.test(value)) {
        return undefined;
    }

    const groupSeparatorPattern = new RegExp(groupPattern, 'g');
    const valueWithoutGroups = value.replace(groupSeparatorPattern, '');
    const normalizedValue = decimal !== '.' ? valueWithoutGroups.replace(decimal, '.') : valueWithoutGroups;

    if (!allowDecimal && !integerInputPattern.test(normalizedValue)) {
        return undefined;
    }
    if (allowDecimal && !simpleDecimalInputPattern.test(normalizedValue)) {
        return undefined;
    }
    return normalizedValue;
};

const normalizeLocaleGroupedNumberInput = (value: string, locale: string, allowDecimal: boolean) =>
    normalizeLocaleGroupedNumber(value, getNumberSeparators(locale), allowDecimal);

export const normalizeIntegerNumberInput = (value: string, locale?: string) => {
    const trimmedValue = value.trim();
    if (integerInputPattern.test(trimmedValue)) {
        return trimmedValue;
    }
    return locale ? normalizeLocaleGroupedNumberInput(trimmedValue, locale, false) : undefined;
};

export const normalizeDecimalNumberInput = (value: string, locale?: string) => {
    const trimmedValue = value.trim();
    return (
        (locale ? normalizeLocaleGroupedNumberInput(trimmedValue, locale, true) : undefined) ??
        (simpleDecimalInputPattern.test(trimmedValue) ? trimmedValue.replace(',', '.') : undefined)
    );
};
